import { api_fetch } from "./client"

export interface TradePromotionChatConversation {
  id: string
  org_id: string
  calendar_id?: string | null
  title?: string | null
  archived_at?: string | null
  created_at: string
  updated_at: string
}

export interface TradePromotionChatMessage {
  id: string
  conversation_id: string
  role: "user" | "assistant"
  content: string
  thinking_text?: string | null
  tool_calls?: unknown
  tool_results?: unknown
  metadata?: unknown
  created_at: string
}

export interface CreateTradePromotionChatConversationRequest {
  calendar_id?: string
  force_new?: boolean
}

export interface SendTradePromotionChatMessageRequest {
  content: string
  current_year?: number
  current_search?: string
}

export interface UpdateTradePromotionChatConversationRequest {
  archived?: boolean
}

export interface TradePromotionChatStreamCallbacks {
  onThinking: (message: string) => void
  onToolCall: (toolName: string) => void
  onTextDelta: (content: string) => void
  onText: (content: string) => void
  onDone: () => void
  onError: (error: string) => void
}

const API_BASE = "/trade-promotion-chat/conversations"

export async function createTradePromotionChatConversation(
  request: CreateTradePromotionChatConversationRequest,
): Promise<TradePromotionChatConversation> {
  const result = await api_fetch<TradePromotionChatConversation>(API_BASE, {
    method: "POST",
    body: request,
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to create TradeWiz conversation: ${result.error.message}`)
}

export async function listTradePromotionChatConversations(calendar_id?: string): Promise<TradePromotionChatConversation[]> {
  const params = new URLSearchParams()
  if (calendar_id) params.append("calendar_id", calendar_id)
  const url = params.toString() ? `${API_BASE}?${params}` : API_BASE
  const result = await api_fetch<TradePromotionChatConversation[]>(url)
  if (result.ok) return result.value.data
  throw new Error(`Failed to list TradeWiz conversations: ${result.error.message}`)
}

export async function getTradePromotionChatMessages(conversation_id: string): Promise<TradePromotionChatMessage[]> {
  const result = await api_fetch<TradePromotionChatMessage[]>(`${API_BASE}/${conversation_id}/messages`)
  if (result.ok) return result.value.data
  throw new Error(`Failed to load TradeWiz messages: ${result.error.message}`)
}

export async function updateTradePromotionChatConversation(
  conversation_id: string,
  request: UpdateTradePromotionChatConversationRequest,
): Promise<TradePromotionChatConversation> {
  const result = await api_fetch<TradePromotionChatConversation>(`${API_BASE}/${conversation_id}`, {
    method: "PATCH",
    body: request,
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to update TradeWiz conversation: ${result.error.message}`)
}

export function sendTradePromotionChatMessageStream(
  conversation_id: string,
  request: SendTradePromotionChatMessageRequest,
  callbacks: TradePromotionChatStreamCallbacks,
): AbortController {
  const abort_controller = new AbortController()

  fetch(`/api${API_BASE}/${conversation_id}/messages/stream`, {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify(request),
    signal: abort_controller.signal,
  })
    .then(async (response: Response) => {
      if (!response.ok) {
        callbacks.onError(`Request failed: ${await response.text()}`)
        return
      }
      const reader = response.body?.getReader()
      if (!reader) {
        callbacks.onError("No response body")
        return
      }
      const decoder = new TextDecoder()
      let buffer = ""
      let done_received = false
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop() || ""
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue
          const data = line.slice(6).trim()
          if (!data) continue
          try {
            const event = JSON.parse(data)
            switch (event.type) {
              case "thinking":
                callbacks.onThinking(event.message)
                break
              case "tool_call":
                callbacks.onToolCall(event.tool_name)
                break
              case "text_delta":
                callbacks.onTextDelta(event.content)
                break
              case "text":
                callbacks.onText(event.content)
                break
              case "done":
                done_received = true
                callbacks.onDone()
                break
              case "error":
                callbacks.onError(event.error)
                break
            }
          } catch (error) {
            console.error("Failed to parse TradeWiz stream event:", data, error)
          }
        }
      }
      if (!done_received && !abort_controller.signal.aborted) callbacks.onDone()
    })
    .catch((error: Error) => {
      if (error.name !== "AbortError") callbacks.onError(error.message)
    })

  return abort_controller
}
